import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { useCallback, useContext } from 'react';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import { FiltersUtils } from '../../../../helpers/search-filters';
import { commifyNumber } from '../../../../helpers/common';
import { CompoundsApis } from '../../../../new-lib/apis/compounds.apis';
import { CompoundSummaryDto } from '../../../map/dtos';

interface EntityProps {
  entity: any;
}

const EntityPrices = ({ entity }: EntityProps) => {
  const { t } = useTranslation('entity');
  const { locale } = useRouter();

  const { searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;

  const mapFiltersToNewKeys = useCallback(() => {
    return FiltersUtils.mapFiltersToNew(searchFiltersState.searchFilters);
  }, [searchFiltersState.searchFilters]);

  const { data: compound, isLoading } = useQuery<CompoundSummaryDto>({
    queryKey: [`compound-summary`, entity.id, searchFiltersState.searchFilters],
    queryFn: ({ signal }) =>
      CompoundsApis.getCompoundSummary(
        entity.id,
        mapFiltersToNewKeys(),
        locale,
        { signal }
      ),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  if (!entity.not_launched && entity.properties_count == 0) {
    return null;
  }

  if (isLoading || !compound) {
    return (
      <Container className="price-split">
        <>
          <div className="price-section">
            <span className="sale-type">{t('developer_start')}</span>
            <ShimmerEffect className="shimmer-effect" />
          </div>
          <div className="separator" />
          <div className="price-section">
            <span className="sale-type">{t('resale_start')}</span>
            <ShimmerEffect className="shimmer-effect" />
          </div>
        </>
      </Container>
    );
  }

  if (compound.isLaunch) {
    return (
      <Container className="price-split">
        <div className="price-section">
          <span>{t('price_tba')}</span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="price-split">
      <div className="price-section">
        <span className="sale-type">{t('developer_start')}</span>
        <span>
          {compound?.prices?.developerStartingPrice ? (
            <>
              <span className="price">
                {commifyNumber(compound.prices.developerStartingPrice)}
              </span>{' '}
              {compound.prices.currency ?? t('egp')}
            </>
          ) : (
            <span>-</span>
          )}
        </span>
      </div>

      <div className="separator" />

      <div className="price-section">
        <span className="sale-type">{t('resale_start')}</span>
        <span>
          {compound?.prices?.resaleStartingPrice ? (
            <>
              <span className="price">
                {commifyNumber(compound.prices.resaleStartingPrice)}
              </span>{' '}
              {compound.prices.currency ?? t('egp')}
            </>
          ) : (
            <span>-</span>
          )}
        </span>
      </div>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 40px;
  align-items: center;
  align-self: center;
  gap: 12px;
  border-radius: 8px;
  z-index: 10;
  width: 350px;
  justify-content: space-between;
  .price-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    span {
      line-height: 150%;
      color: #2c3138;
      font-family: materialMedium;
      font-size: 12px;
      text-transform: capitalize;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
      &.sale-type {
        font-family: materialRegular;
        font-size: 16px;
        width: max-content;
      }
    }
    .price {
      color: #2c3138;
      text-align: center;
      font-family: materialSemiBold;
      font-size: 22px;
      line-height: 150%;
    }
  }

  .separator {
    width: 1px;
    height: 70px;
    background: #d1d4d9;
  }

  @media screen and (max-width: 768px) {
    justify-content: space-evenly;
    .price-section {
      span {
        &.sale-type {
          font-size: 12px;
        }
      }
      .price {
        font-size: 18px;
      }
    }
    .separator {
      width: 1px;
      height: 50px;
      background: #d1d4d9;
    }
  }
`;

const ShimmerEffect = styled.div`
  width: 120px;
  height: 22.5px;
  background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% auto;
  animation: shimmer 1.2s infinite;
  border-radius: 8px;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

export default EntityPrices;
